import type { Case } from '../types/case';
import { formatCurrency } from '../lib/format';

interface Props {
  case: Case;
}

interface PolicyShape {
  max_plan_months?: number;
  min_plan_months?: number;
  fee_waiver_cap_pct?: number;
  max_discount_pct?: number;
  max_settlement_amount?: number;
  require_down_payment?: boolean;
  [k: string]: unknown;
}

const humanize = (key: string): string =>
  key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatValue = (key: string, value: unknown): string => {
  if (value == null) return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'number') {
    if (key.endsWith('_pct')) return `${value}%`;
    if (key.includes('months')) return `${value} months`;
    if (key.includes('amount') || key.includes('cap')) return formatCurrency(value);
    return String(value);
  }
  return String(value);
};

/**
 * Renders policy constraints surfaced via `policy_overrides` on the case.
 * If the Resolution Engine later returns the resolved policy directly, we can
 * swap this to consume that — the UI shape is the same.
 */
export function PolicyConstraints({ case: c }: Props) {
  const policy = c.policy_overrides as PolicyShape;
  const entries = Object.entries(policy).filter(([, v]) => v !== null && v !== undefined);

  if (entries.length === 0) {
    return (
      <section className="card p-4">
        <div className="section-label">Policy constraints</div>
        <p className="mt-2 text-sm text-gray-500">
          Using the default operator policy. No case-specific overrides.
        </p>
      </section>
    );
  }

  return (
    <section className="card p-4">
      <div className="section-label">Policy constraints</div>
      <ul className="mt-2 space-y-1 text-sm">
        {entries.map(([key, value]) => (
          <li key={key} className="flex items-center justify-between gap-3">
            <span className="text-gray-600">{humanize(key)}</span>
            <span className="font-medium">{formatValue(key, value)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}
